// @ts-check
/* global $ module require plupload */
'use_strict';

declare var plupload;

import { resize_last_dialog } from '../tv_utils/utils.js';
import { ProgressBar } from '../tv_utils/progress_bar';
import { Notifications } from '../tv_utils/notifications';
import { DIFUUploadQueue } from './difu_upload_queue';

/**
 * Class to initialize a plupload object and bind its functionality to ui elements.
 */
export class ArtworkUploader {
    /**
     * @var {string} id inventtrans id of the artwork line to create uploader for.
     * @var {string} extension_string comma delimited string of file accepted file extensions.
     * @var {Array<string>} extensions The list of extensions, computed from the extension_string.
     * @var {[key: string]: any} elements dict of related HTMLElements on the dom.
     * @var {ProgressBar} progress_bar A handle to the instance of a progress bar.
     * @var {plupload.Uploader} uploader A handle to the instace of the pl upload Uploader class.
     * @var {[key: string]: any} upload_params parameters to be passed to the controller when file is uploaded.
     * @var {string} dispatch url for upload.
     * @var {NodeJS.Timer} init_timeout to allow for cancellation of init call if destroyed before loaded.
     */
    id: string;
    extension_string: string;
    extensions: Array<string>;
    elements: {[key: string]: any};
    progress_bar: ProgressBar;
    uploader: any;
    upload_params: {[key: string]: any};
    dispatch: string;
    init_timeout: NodeJS.Timer;

    /**
     * The constructor for the class. Sets up the progress bar and uploader classes.
     *
     * @param {[key: string]: any} upload_params to be passed to controller when file is uploaded.
     * @param {string} id The inventtrans_id for the artwork.
     * @param {string} dispatch controller url for upload.
     * @param {string} extension_string The comma delimited list of valid extension strings.
     * @return {GenericUploadQueue} instantiated and bound.
     */
    constructor(upload_params: {[key: string]: any}, id: string, dispatch: string, extension_string: string) {
        this.upload_params = upload_params;
        this.id = id;
        this.dispatch = `${window.location.origin}/${dispatch}`;
        this.extension_string = extension_string;
        this.extensions = extension_string.split(',');
        this.elements = {};
        this.progress_bar = new ProgressBar(`upload-progress-${this.id}`);

        this.uploader = this.init_uploader();
        // Set a timeout for the init call to allow for cancellation if destroyed before loaded.
        this.init_timeout = setImmediate(() => {
            this.uploader.init();
        });

        this.bind();
    }

    /**
     * Bind the different button click and text input change events.
     *
     * @returns {void}
     */
    bind(): void {
        this.elements = {
            choose_file_btn: document.getElementById(`js-btnPickfiles-${this.id}`),
            submit_btn: document.getElementById(`js-btnSubmit-${this.id}`),
            progress_bar_container: document.getElementById(`progress-bar-container-${this.id}`),
            chosen_artwork_container: document.getElementById(`tv-artwork-chosen-line-content-${this.id}`),
            artwork_is_uploaded: document.getElementById(`js-artwork-is-uploaded-${this.id}`),
            is_difu_upload: document.getElementById(`js-artwork-is-difu-${this.id}`),
            display_filename: document.getElementById(`js-pickfile-display-${this.id}`),
            close_cart_modal_btn: document.getElementById(`js-artwork-upload-modal-cart-close-btn`),

            // DIFU modal opener button present on Upload Artwork page.
            difu_modal_btn: document.getElementById(`js-difu-modal-opener-${this.id}`), 

            // Required Inputs for Artwork Upload
            image_description: document.getElementById(`js-txtImageDescription-${this.id}`),
            file_name: document.getElementById(`js-filename-${this.id}`),
            notes: document.getElementById(`js-notes-${this.id}`)
        }

        // If Artwork has already been chosen for this Design, then we need to provide the ability to change that.
        const artwork_chosen = document.getElementById(`tv-artwork-chosen-${this.id}`);
        const change_artwork = document.getElementById(`tv-artwork-change-${this.id}`);
        if (this.elements.chosen_artwork_container && this.elements.progress_bar_container) {
            if (this.elements.artwork_is_uploaded && this.elements.artwork_is_uploaded.value) {
                this.elements.chosen_artwork_container.classList.remove('tv-display__none');
                this.elements.progress_bar_container.classList.add('tv-display__none');
            } else {
                this.elements.chosen_artwork_container.classList.add('tv-display__none');
                this.elements.progress_bar_container.classList.remove('tv-display__none');
            }
        }

        if (artwork_chosen && change_artwork && this.elements.chosen_artwork_container && this.elements.progress_bar_container) {
            artwork_chosen.addEventListener('click', () => {
                this.elements.chosen_artwork_container.classList.add('tv-display__none');
                this.elements.progress_bar_container.classList.remove('tv-display__none');
            });
        }

        // Hide change button if DIFU order
        if (this.elements.is_difu_upload && this.elements.is_difu_upload.value == 'difu') {
            change_artwork.classList.add('tv-display__none');
        }

        if (this.elements.choose_file_btn) {
            this.elements.choose_file_btn.addEventListener('click', () => {
                document.getElementById(`js-pickfiles-${this.id}`).click();
            });
        }

        // Trigger the correct action when the 'Upload Artwork' button is clicked.
        if (this.elements.submit_btn) {
            this.elements.submit_btn.addEventListener('click', this.start_upload.bind(this), false);
        }

        // Close cart modal when the close button is clicked.
        if (this.elements.close_cart_modal_btn) {
            this.elements.close_cart_modal_btn.addEventListener('click', () => {
                const modal_close = document.querySelector('.ui-dialog-titlebar-close') as HTMLElement;
                modal_close.click();
            });
        }
    }

    /**
     * Instantiates an instance of a plupload uploader.
     *
     * @returns {plupload.Uploader} instance of a plupload uploader.
     */
    init_uploader() {
        const uploader = new plupload.Uploader({
            runtimes: 'html5',
            browse_button: `js-pickfiles-${this.id}`,
            container: `container-${this.id}`,
            max_file_size: '1536mb',
            chunk_size: '5mb',
            url: this.dispatch,
            flash_swf_url: 'js/addons/tv_artwork/plupload.flash.swf',
            silverlight_xap_url: 'js/addons/tv_artwork/plupload.silverlight.xap',
            filters: [
                {title: 'Artwork Files', extensions: this.extension_string}
            ],
            init: {
                BeforeUpload: this.set_upload_params.bind(this),
                FilesAdded: this.upload_files_added.bind(this),
                UploadProgress: this.upload_progress.bind(this),
                UploadComplete: this.upload_complete.bind(this)
            }
        });

        return uploader;
    }

    /**
     * Sets the upload parameters for plupload to be sent along in the request to the controller.
     *
     * @returns {void}
     */
    set_upload_params(): void {
        this.upload_params.image_description = this.elements.image_description.value;

        // Set the Journal Entry if notes are available.
        const tones = this.elements.notes.value.trim();
        if (tones) {
            this.upload_params.journal_entry = {
                timestamp: Math.floor(new Date().getTime()/1000.0),
                note: tones
            };
        }

        this.uploader.settings.multipart_params = this.upload_params;
    }

    /**
     * Method to check that all files that are to be uploaded match our accepted file extensions.
     *
     * @param {plupload.Uploader} uploader_instance for plupload.
     * @param {array} files of the files to be added to the upload queue.
     * @returns {void}
     */
    upload_files_added(uploader_instance, files): void {
        for (const i in files) {
            // check if file type is accepted
            if (!this.check_file_extension(files[i].name, true)) {
                // if not accepted remove file from upload list and pop alert
                this.uploader.removeFile(files[i]);
                Notifications.set_notification(
                    'E',
                    'Upload Error.',
                    `File type (extension) not accepted for: ${files[i].name}`,
                    false
                );
            } else {
                // Display the filename as needed.
                if (this.elements.display_filename) {
                    this.elements.display_filename.classList.remove('tv-upload-file-error-highlight');
                }

                if (files[i].name.length > 30) {
                    if (this.elements.display_filename) {
                        this.elements.display_filename.innerHTML = files[i].name.slice(0, 30) + "...";
                    } else {
                        this.elements.file_name.innerHTML = files[i].name.slice(0, 30) + "...";
                    }
                } else {
                    if (this.elements.display_filename) {
                        this.elements.display_filename.innerHTML = files[i].name;
                    } else {
                        this.elements.file_name.value = files[i].name;
                    }
                }
            }
        }
    }

    /**
     * Updates the progress bar with the file upload percentage.
     *
     * @param {plupload.Uploader} uploader_instance for plupload.
     * @param {Object} file plupload object.
     * @returns {void}
     */
    upload_progress(uploader_instance, file): void {
        // Update progress bar to reflect percentage of transfer
        this.progress_bar.set_value(file.percent);
    }

    /**
     * Checks that if a file failed uploaded that an error notification is thrown and if it was a success
     * the upload fields get hidden.
     *
     * @param {plupload.Uploader} uploader_instance for plupload.
     * @param {array} files array of the files that have been uploaded.
     * @returns {void}
     */
    upload_complete(uploader_instance, files): void {
        // Check to see if the file failed upload and display an error notification.
        if (files[0].status === plupload.FAILED) {
            this.elements.progress_bar_container.style.display = 'block';
            this.progress_bar.reset();

            Notifications.set_notification(
                'E',
                'Failed to upload artwork.',
                'tv_artwork_order_upload_error',
                true
            );

            files[0].status = plupload.QUEUED;
            uploader_instance.state = plupload.QUEUED;
            uploader_instance.trigger('StateChanged');

            return;
        }

        // Remove the row from the list after completed upload
        const upload_complete_element = document.getElementById(`js-upload-complete-${this.id}`);
        upload_complete_element.classList.remove('tv-display__none');
        upload_complete_element.classList.add('tv-display__block');
        const upload_status = document.getElementsByClassName(`js-status-${this.id}`);
        for (const status of upload_status) {
            status.innerHTML = 'Artwork Uploaded';
        }

        const event = new Event('upload_complete');
        this.elements.submit_btn.dispatchEvent(event);

        // Create an array of artwork upload statuses. If they are all complete, hide top content
        const all_equal = arr => arr.every(item => item === arr[0]);
        const js_upload_modal_content = document.getElementById('js-artwork-modal-content');
        const js_upload_status_displays = document.getElementsByClassName('js-upload-status');
        let upload_statuses = [];
        const mobile_view = window.innerWidth <= 767;

        for (const js_upload_status_display of js_upload_status_displays) {
            upload_statuses.push(js_upload_status_display.innerHTML);
        }
        
        if (all_equal(upload_statuses) && mobile_view && upload_statuses[0] === 'Artwork Uploaded') {
            document.getElementById('js-artwork-upload-top-content').classList.add('tv-display__none');
            document.getElementById('js-artwork-upload-bottom-content').classList.add('tv-display__none');
            js_upload_modal_content.classList.add('tv-upload-modal-content');
        }

        if (this.elements.chosen_artwork_container && this.elements.progress_bar_container) {
            this.elements.chosen_artwork_container.classList.remove('tv-display__none');
            this.elements.progress_bar_container.classList.add('tv-display__none');
        }
        
        const upload_artwork_modal_button_text = document.getElementById(`js-upload-artwork-button-text-${this.upload_params.configuration_id}`);
        const upload_photo_modal_button_text = document.getElementById(`js-upload-photo-button-text-${this.upload_params.configuration_id}`);
        const total_art_count = document.getElementById(`js-total-art-count-${this.upload_params.configuration_id}`) as HTMLInputElement;
        const no_template_upload = document.getElementById(`no-template-upload-${this.upload_params.configuration_id}`) as HTMLInputElement;

        if (upload_photo_modal_button_text && no_template_upload.value == '1' && total_art_count.value == '1') {
            upload_photo_modal_button_text.innerText =  'Change Uploaded Photo';
        } else if (upload_artwork_modal_button_text && total_art_count.value == '1') {
            upload_artwork_modal_button_text.innerText =  'Change Uploaded Template';
        }
    }

    /**
     * Checks whether a file names extension matches a list of accepted or rejected extensions.
     *
     * @param {string} file_name the file name to check.
     * @param {boolean} search_accepted_exts flag to indicate whether to check against accepted extensions.
     * @returns {boolean} indicates whether file name extension matched an entry in the list.
     */
    check_file_extension(file_name: string, search_accepted_exts: boolean): boolean {
        //  Get file extension.
        const file_ext = file_name.split('.').pop().toLowerCase();

        if (search_accepted_exts) {
            // Check file ext against accepted extensions, if it matches one of those, accept the file.
            return this.extensions.includes(file_ext);
        }

        // Check file ext against rejected extensions, if it doesn't match one of those, accept the file.
        return !this.extensions.includes(file_ext);
    }

    /**
     * Starts the upload process.
     *
     * @param {Event} event The click event for the upload button.
     * @returns {void}
     */
    start_upload(event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        this.elements.image_description.value = this.elements.image_description.value.trim();

        if (this.elements.image_description.value == '') {
            this.elements.image_description.setCustomValidity('Please fill out this field.');
            this.elements.image_description.reportValidity();
        }

        this.elements.image_description.setCustomValidity('');

        // Handle file upload validation.
        this.elements.display_filename.classList.remove('tv-upload-file-error-highlight');
        if (this.elements.display_filename.innerHTML == "No File Selected" || this.elements.display_filename.innerHTML == "Please select a file.") {
            this.elements.display_filename.classList.add('tv-upload-file-error-highlight');
            this.elements.display_filename.innerHTML = "Please select a file.";
            return;
        }
        
        // Check the required field values.
        if (this.elements.image_description.reportValidity()
            && this.uploader.files.length
        ) {
            // Hide the file details.
            this.elements.progress_bar_container.classList.add('tv-display__none');
            // Hide additional buttons present on Upload Artwork page.
            if (this.elements.difu_modal_btn) {
                this.elements.difu_modal_btn.classList.add('tv-display__none');
                this.elements.submit_btn.classList.add('tv-display__none');
            }
            // Display the progress bar.
            this.progress_bar.start();
            // Remove all but most recently submitted image.
            for (let i = this.uploader.files.length; i > 1; i--) {
                this.uploader.files.shift();
            }
            // Start the upload.
            this.uploader.start();

            // Update call to action
            this.update_cta();

        }
        resize_last_dialog();
    }

    /**
     * Updates the text and classes on the call-to-action buttons.
     * 
     * @returns {void}
     */
    update_cta() {
        let js_upload_design_btn = Array.from(document.getElementsByClassName('js-upload-design-btn') as HTMLCollectionOf<HTMLElement>);
        let upload_btn_array_check = new Array();
        const artwork_modal_add_to_cart_btn = document.getElementsByClassName('js-artwork-upload-btn')[0];
        const design_step_icons = document.getElementsByClassName('tv-checkout-progress-icon-container');
        const checkout_step_icon = document.getElementById('tv-progress-add-to-cart-icon');
        const artwork_requirements_content = document.getElementById('js-artwork-upload-bottom-content');
        const upload_next_artwork_btn = document.getElementById('js-artwork-upload-next-btn');
        let check = (list) => list.every(item => list.indexOf(item) === 0);

        if (design_step_icons && design_step_icons.length > 0) {
            for (let i = 0; i < js_upload_design_btn.length; i++) {
                upload_btn_array_check.push(js_upload_design_btn[i].parentElement.classList.contains('tv-display__none'));
                if (js_upload_design_btn[i].parentElement.classList.contains('tv-display__none')) {
                    design_step_icons[i].classList.add('tv-checkout-step-completed');
                    upload_next_artwork_btn.classList.remove('tv-display__none');
                }
            }
        }

        // Hide and display artwork uploaders with the next button.
        if (upload_next_artwork_btn) {
            upload_next_artwork_btn.addEventListener('click', this.upload_next_artwork);
        }

        // If all upload template / photo buttons are hidden, update CTA button
        if (js_upload_design_btn && artwork_modal_add_to_cart_btn && check(upload_btn_array_check)) {
            artwork_modal_add_to_cart_btn.innerHTML = "Add to Cart";
            artwork_modal_add_to_cart_btn.classList.remove('ty-btn__tertiary');
            artwork_modal_add_to_cart_btn.classList.add('ty-btn__primary');
            document.getElementById('js-modal-do-later-text')?.classList.add('tv-display__none');
            upload_next_artwork_btn.classList.add('tv-display__none');
            if (checkout_step_icon) {
                checkout_step_icon.classList.add("tv-add-to-cart-icon-ready");
            }
            if (artwork_requirements_content) {
                artwork_requirements_content.classList.add('tv-display__none');
            }
        }
    }

    /**
     * Applies click event listener for next artwork button without applying it exponentially with each design.
     * 
     * @returns {void}
     */
    upload_next_artwork() {
        const current_design_display = document.getElementById('js-current-design');
        const artwork_uploader_row = document.getElementsByClassName('js-artwork-uploader-row');
        const js_upload_design_btn = Array.from(document.getElementsByClassName('js-upload-design-btn') as HTMLCollectionOf<HTMLElement>);
        const upload_next_artwork_btn = document.getElementById('js-artwork-upload-next-btn');

        for (let i = 0; i < js_upload_design_btn.length; i++) {
            if (js_upload_design_btn[i].parentElement.classList.contains('tv-display__none')) {
                artwork_uploader_row[i].classList.add('tv-display__none');
                artwork_uploader_row[i+1].classList.remove('tv-display__none');
            }
        }

        current_design_display.innerHTML = (parseInt(current_design_display.innerHTML) + 1).toString();
        upload_next_artwork_btn.classList.add('tv-display__none');
        upload_next_artwork_btn.removeEventListener('click', this.upload_next_artwork);
    }

    /**
     * Cancels initialization then destroys uploader.
     *
     * @returns {void}
     */
    destroy_uploader() {
        clearImmediate(this.init_timeout);
        this.uploader.destroy();
    }
}

export function init_artwork_uploaders(): void {
    const extensions = (<HTMLInputElement>document.getElementById('js-artwork_extensions')).value;
    const artwork_lines = document.getElementsByClassName('js-artwork_line');

    for (const l of artwork_lines) {
        const line = (<HTMLInputElement>l);
        const params = {
            'sales_id': line.dataset.sales_id,
            'inventtrans_id': line.dataset.id
        };

        (() => new ArtworkUploader(
            params,
            line.dataset.id,
            'index.php?dispatch=tv_artwork.upload_order',
            extensions
        ))();

        const uploader_ids = document.querySelectorAll(`.tv_uploader_ids[value='${line.dataset.id}']`);
        for (const up_id of uploader_ids) {
            const uploader_id = (<HTMLInputElement>up_id).value;
            // This needs an uploader_id, and sales_id.
            (() => new DIFUUploadQueue(uploader_id, line.dataset.sales_id))();
        }
    }
}